import { useState, useRef } from "react";
import { MATERIALS } from "@/lib/types";
import type { PrintSettings } from "@/lib/types";
import { api } from "@/lib/api";
import { useRouter } from "@/lib/router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Upload, FileUp, Box, Gauge, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export function UploadPage() {
	const { navigate } = useRouter();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [stlFile, setStlFile] = useState<File | null>(null);
	const [tagInput, setTagInput] = useState("");
	const [tags, setTags] = useState<string[]>([]);
	const [submitting, setSubmitting] = useState(false);
	const [dragOver, setDragOver] = useState(false);

	const [settings, setSettings] = useState<PrintSettings>({
		material: "PLA",
		nozzleTemp: 210,
		bedTemp: 60,
		layerHeight: 0.2,
		infillPercent: 20,
		printSpeed: 50,
		supports: false,
		notes: "",
	});

	function handleFile(file: File) {
		if (file.name.toLowerCase().endsWith(".stl")) {
			setStlFile(file);
		}
	}

	function handleDrop(e: React.DragEvent) {
		e.preventDefault();
		setDragOver(false);
		const file = e.dataTransfer.files[0];
		if (file) handleFile(file);
	}

	function addTag() {
		const tag = tagInput.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
		if (tag && !tags.includes(tag) && tags.length < 8) {
			setTags([...tags, tag]);
			setTagInput("");
		}
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!stlFile || !title.trim()) return;

		setSubmitting(true);
		try {
			const uploaded = await api.upload(stlFile);
			const project = await api.projects.create({
				title: title.trim(),
				description: description.trim(),
				creatorId: "u1",
				stlFileName: uploaded.name,
				stlFileSize: uploaded.size,
				printSettings: settings,
				tags,
			});
			navigate(`/project/${project.id}`);
		} catch {
			setSubmitting(false);
		}
	}

	function updateSetting<K extends keyof PrintSettings>(key: K, value: PrintSettings[K]) {
		setSettings((prev) => ({ ...prev, [key]: value }));
	}

	return (
		<div className="max-w-3xl mx-auto space-y-6">
			<div>
				<h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
					<Upload className="h-6 w-6 text-orange-400" />
					Upload Project
				</h1>
				<p className="text-sm text-muted-foreground mt-1">
					Share your 3D print with the community. Upload your STL and proven print settings.
				</p>
			</div>

			<form onSubmit={handleSubmit} className="space-y-6">
				{/* STL Upload */}
				<Card className="border-border/50 bg-card/50">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm flex items-center gap-2">
							<FileUp className="h-4 w-4 text-orange-400" />
							STL File
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div
							onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
							onDragLeave={() => setDragOver(false)}
							onDrop={handleDrop}
							onClick={() => fileInputRef.current?.click()}
							className={cn(
								"border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all",
								dragOver
									? "border-orange-500 bg-orange-500/5"
									: stlFile
									? "border-emerald-500/50 bg-emerald-500/5"
									: "border-border/50 hover:border-border hover:bg-muted/30"
							)}
						>
							<input
								ref={fileInputRef}
								type="file"
								accept=".stl"
								onChange={(e) => {
									const f = e.target.files?.[0];
									if (f) handleFile(f);
								}}
								className="hidden"
							/>
							{stlFile ? (
								<div className="flex flex-col items-center gap-2">
									<Box className="h-10 w-10 text-emerald-400" />
									<span className="text-sm font-medium">{stlFile.name}</span>
									<span className="text-xs text-muted-foreground">
										{(stlFile.size / 1000000).toFixed(1)} MB
									</span>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={(e) => { e.stopPropagation(); setStlFile(null); }}
										className="text-xs text-muted-foreground"
									>
										<X className="h-3 w-3 mr-1" /> Remove
									</Button>
								</div>
							) : (
								<div className="flex flex-col items-center gap-2 text-muted-foreground">
									<FileUp className="h-10 w-10 text-muted-foreground/40" />
									<span className="text-sm font-medium">Drop your STL file here</span>
									<span className="text-xs">or click to browse</span>
								</div>
							)}
						</div>
					</CardContent>
				</Card>

				{/* Project Info */}
				<Card className="border-border/50 bg-card/50">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm flex items-center gap-2">
							<Box className="h-4 w-4 text-orange-400" />
							Project Details
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<Label className="text-xs">Title</Label>
							<Input
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								placeholder="e.g., Articulated Dragon V3"
								className="mt-1"
								required
							/>
						</div>
						<div>
							<Label className="text-xs">Description</Label>
							<Textarea
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								placeholder="Describe your model, print tips, assembly instructions..."
								className="mt-1"
								rows={4}
							/>
						</div>
						<div>
							<Label className="text-xs">Tags</Label>
							<div className="flex gap-2 mt-1">
								<Input
									value={tagInput}
									onChange={(e) => setTagInput(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") { e.preventDefault(); addTag(); }
									}}
									placeholder="Add a tag..."
									className="flex-1"
								/>
								<Button type="button" variant="outline" size="sm" onClick={addTag}>
									Add
								</Button>
							</div>
							{tags.length > 0 && (
								<div className="flex flex-wrap gap-1.5 mt-2">
									{tags.map((tag) => (
										<Badge key={tag} variant="secondary" className="text-xs gap-1">
											{tag}
											<button type="button" onClick={() => setTags(tags.filter((t) => t !== tag))}>
												<X className="h-3 w-3" />
											</button>
										</Badge>
									))}
								</div>
							)}
						</div>
					</CardContent>
				</Card>

				{/* Print Settings */}
				<Card className="border-border/50 bg-card/50">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm flex items-center gap-2">
							<Gauge className="h-4 w-4 text-orange-400" />
							Print Settings
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
							<div>
								<Label className="text-xs">Material</Label>
								<select
									value={settings.material}
									onChange={(e) => updateSetting("material", e.target.value)}
									className="w-full mt-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
								>
									{MATERIALS.map((m) => (
										<option key={m} value={m}>{m}</option>
									))}
								</select>
							</div>
							<div>
								<Label className="text-xs">Nozzle Temp (°C)</Label>
								<Input
									type="number"
									value={settings.nozzleTemp}
									onChange={(e) => updateSetting("nozzleTemp", Number(e.target.value))}
									className="mt-1"
								/>
							</div>
							<div>
								<Label className="text-xs">Bed Temp (°C)</Label>
								<Input
									type="number"
									value={settings.bedTemp}
									onChange={(e) => updateSetting("bedTemp", Number(e.target.value))}
									className="mt-1"
								/>
							</div>
							<div>
								<Label className="text-xs">Layer Height (mm)</Label>
								<Input
									type="number"
									step="0.01"
									value={settings.layerHeight}
									onChange={(e) => updateSetting("layerHeight", Number(e.target.value))}
									className="mt-1"
								/>
							</div>
							<div>
								<Label className="text-xs">Infill (%)</Label>
								<Input
									type="number"
									value={settings.infillPercent}
									onChange={(e) => updateSetting("infillPercent", Number(e.target.value))}
									className="mt-1"
								/>
							</div>
							<div>
								<Label className="text-xs">Speed (mm/s)</Label>
								<Input
									type="number"
									value={settings.printSpeed}
									onChange={(e) => updateSetting("printSpeed", Number(e.target.value))}
									className="mt-1"
								/>
							</div>
						</div>

						<div className="flex items-center gap-2">
							<input
								type="checkbox"
								id="supports"
								checked={settings.supports}
								onChange={(e) => updateSetting("supports", e.target.checked)}
								className="h-4 w-4 rounded border-input"
							/>
							<Label htmlFor="supports" className="text-sm cursor-pointer">
								Supports required
							</Label>
						</div>

						<div>
							<Label className="text-xs">Print Notes</Label>
							<Textarea
								value={settings.notes}
								onChange={(e) => updateSetting("notes", e.target.value)}
								placeholder="Special instructions, tips, known issues..."
								className="mt-1"
								rows={3}
							/>
						</div>
					</CardContent>
				</Card>

				<Button
					type="submit"
					disabled={submitting || !stlFile || !title.trim()}
					className="w-full h-11 bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 font-medium gap-2"
				>
					{submitting ? (
						<>
							<Loader2 className="h-4 w-4 animate-spin" /> Uploading...
						</>
					) : (
						<>
							<Upload className="h-4 w-4" /> Publish Project
						</>
					)}
				</Button>
			</form>
		</div>
	);
}
